import { useSyncExternalStore } from "react";

/**
 * Per-user ephemeral LLM key.
 *
 * Secret discipline (spec Appendix D, FE-2): the key lives in `sessionStorage`
 * only — never `localStorage` — and is never sent to the Istari backend except
 * as the `X-LLM-Api-Key` header on the chat stream. It is cleared on logout and
 * whenever the backend rejects it (`token_expired`).
 */
const STORAGE_KEY = "istari.ai.llm-api-key";

let promptOpen = false;
// In-memory fallback for when sessionStorage is unavailable (private mode / disabled): without it the key
// wouldn't survive even the same tab session, so BYOK would silently never work there.
let memoryKey: string | null = null;
const listeners = new Set<() => void>();

function emit() {
  for (const listener of listeners) listener();
}

function readKey(): string | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    if (stored !== null) return stored;
  } catch {
    // sessionStorage unavailable — fall through to the in-memory fallback.
  }
  return memoryKey;
}

export function getLlmApiKey(): string | null {
  const key = readKey();
  return key !== null && key.length > 0 ? key : null;
}

export function setLlmApiKey(key: string): void {
  memoryKey = key;
  try {
    sessionStorage.setItem(STORAGE_KEY, key);
  } catch {
    // sessionStorage unavailable (private mode / disabled) — the in-memory fallback keeps the key for this
    // tab session; it just won't survive a reload.
  }
  promptOpen = false;
  emit();
}

export function clearLlmApiKey(): void {
  memoryKey = null;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // nothing to clear in storage if it's unavailable.
  }
  emit();
}

export function openLlmKeyPrompt(): void {
  promptOpen = true;
  emit();
}

export function closeLlmKeyPrompt(): void {
  promptOpen = false;
  emit();
}

function subscribe(callback: () => void): () => void {
  listeners.add(callback);
  return () => {
    listeners.delete(callback);
  };
}

export interface LlmApiKeyState {
  hasKey: boolean;
  promptOpen: boolean;
}

export function useLlmApiKey(): LlmApiKeyState {
  const hasKey = useSyncExternalStore(
    subscribe,
    () => getLlmApiKey() !== null,
    () => false,
  );
  const isPromptOpen = useSyncExternalStore(
    subscribe,
    () => promptOpen,
    () => false,
  );
  return { hasKey, promptOpen: isPromptOpen };
}
